@if (loading) {
  <app-loader></app-loader>
} @else {
  <mat-stepper
    linear
    [style.height]="stepperHeight"
    (selectionChange)="onSelectionChange($event.selectedIndex)"
  >
    <ng-template matStepperIcon="edit">
      <mat-icon>check</mat-icon>
    </ng-template>
    <mat-step [stepControl]="gettingStartedFormGroup">
      <form class="flex flex-col" [formGroup]="gettingStartedFormGroup">
        <ng-template matStepLabel>Getting started</ng-template>
        <div class="getting-started-container flex flex-row gap-8">
          <div class="flex flex-col gap-4 basis-[50%] half-width-container">
            <div class="flex flex-col gap-4">
              <div>
                <h3>
                  Project <i>{{ opportunityName }}</i>
                </h3>
                <h3>Let's name your sheet</h3>
                <p>You can add multiple sheets to your data capture.</p>
              </div>
              <mat-form-field>
                <mat-label>Name your sheet</mat-label>
                <input
                  matInput
                  formControlName="sheetName"
                  data-test-id="sheet-get-started-create-sheet-name-input"
                />
                @if (sheetName.hasError('required')) {
                  <mat-error data-test-id="sheet-get-started-sheet-name-error">
                    Your sheet name is required
                  </mat-error>
                }
                @if (sheetName.hasError('invalidName')) {
                  <mat-error data-test-id="sheet-get-started-sheet-name-error">
                    Your sheet name must not contain the following characters [
                    ? : / [ \ ] * ]
                  </mat-error>
                }
                @if (
                  sheetName.hasError('minlength') ||
                  sheetName.hasError('maxlength')
                ) {
                  <mat-error data-test-id="sheet-get-started-sheet-name-error">
                    Your sheet name must be between 3 and 31 characters
                  </mat-error>
                }
                @if (sheetName.hasError('duplicateName')) {
                  <mat-error data-test-id="sheet-get-started-sheet-name-error">
                    Your sheet name already exists
                  </mat-error>
                }
              </mat-form-field>
            </div>
          </div>
          <div class="flex flex-col gap-4 basis-[50%] half-width-container">
            <app-skeleton-data-capture
              [sheetName]="sheetName.value"
              [segments]="selectedSegments"
            ></app-skeleton-data-capture>
          </div>
        </div>
        <div class="progress-container">
          <button
            mat-button
            matStepperPrevious
            data-test-id="sheet-get-started-cancel-sheet-creation"
            (click)="cancelCreation()"
          >
            Cancel
          </button>
          <ng-container *ngTemplateOutlet="helpLinkTemplate"></ng-container>
          <button
            mat-button
            matStepperNext
            data-test-id="sheet-get-started-move-to-next-step"
          >
            Next
          </button>
        </div>
      </form>
    </mat-step>
    <mat-step [stepControl]="segmentsFormGroup">
      <form class="flex flex-col" [formGroup]="segmentsFormGroup">
        <ng-template matStepLabel>Select your segments</ng-template>
        <div class="getting-started-container flex flex-row gap-8">
          <div class="flex flex-col gap-4 basis-[50%] half-width-container">
            <div class="flex flex-col gap-4">
              <div>
                <h3>
                  Let's link your segments for sheet
                  <i>{{ sheetName.value }}</i>
                </h3>
                <p>
                  Segments are groups of experts that you can assign to your
                  data capture sheet. You can assign one, many or all segments
                  within your project to your sheet.
                </p>
              </div>
              <mat-form-field>
                <mat-label>Choose segment(s)</mat-label>
                <mat-select
                  multiple
                  formControlName="segments"
                  data-test-id="sheet-get-started-segment-selection-dropdown"
                >
                  @for (segment of segments; track segment) {
                    <mat-option
                      data-test-id="sheet-get-started-segment-selection-option"
                      [value]="segment.segmentId"
                      [disabled]="segment.occupied"
                    >
                      {{ segment.segmentName }}
                    </mat-option>
                  }
                </mat-select>
                <mat-error
                  data-test-id="sheet-get-started-segment-selection-error"
                  >At least one segment must be selected
                </mat-error>
              </mat-form-field>
              <p>
                We recommend creating one sheet per unique discussion guide. For
                example, if you are asking Payers different questions to KOLs,
                create one data sheet for Payers, and another for KOLs.
              </p>
            </div>
          </div>
          <div class="flex flex-col gap-4 basis-[50%] half-width-container">
            <app-skeleton-data-capture
              [sheetName]="sheetName.value"
              [segments]="selectedSegments"
            ></app-skeleton-data-capture>
          </div>
        </div>
        <div class="progress-container">
          <button
            mat-button
            matStepperPrevious
            data-test-id="sheet-get-started-move-to-previous-step"
          >
            Back
          </button>
          <ng-container *ngTemplateOutlet="helpLinkTemplate"></ng-container>
          <button
            mat-button
            matStepperNext
            data-test-id="sheet-get-started-move-to-next-step"
          >
            Next
          </button>
        </div>
      </form>
    </mat-step>
    <mat-step [stepControl]="themesFormGroup">
      <form
        class="flex flex-col"
        [formGroup]="themesFormGroup"
        (submit)="onThemesFormSubmit()"
      >
        <ng-template matStepLabel
          >Add interview sections & questions</ng-template
        >
        <div class="discussion-guide-container flex flex-row gap-8">
          <div
            class="flex flex-col gap-4 flexible-half-width-container"
            [class.full-width-preview-editor]="collapsedPreview"
            [class.half-width-preview-editor]="!collapsedPreview"
          >
            <div>
              <h3>
                Let's set up your discussion guide for sheet
                <i>{{ sheetName.value }}</i>
              </h3>
              <p class="m-0">
                Import your discussion guide, or add questions manually below.
                They will display as your column headers and can be edited at
                any time.
              </p>
            </div>
            @if (!baseDiscussionGuideInput) {
              <div class="dg-rules">
                <div class="dg-rule">
                  <mat-icon>title</mat-icon>
                  <div>
                    <div class="dg-rule-title">Interview section titles</div>
                    <div class="dg-rule-subtitle">
                      Use <strong>Heading 1</strong>
                    </div>
                  </div>
                </div>
                <div class="dg-rule">
                  <mat-icon>help_outline</mat-icon>
                  <div>
                    <div class="dg-rule-title">Questions</div>
                    <div class="dg-rule-subtitle">
                      Use <strong>Heading 2</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div class="dg-upload-row">
                <span class="dg-file-name">
                  {{
                    discussionGuideFile?.value
                      ? discussionGuideFile.value.name
                      : 'No file selected'
                  }}
                </span>
                @if (discussionGuideFile?.hasError('unknownFormat')) {
                  <mat-error> Please select a valid .docx file. </mat-error>
                }
                <span class="dg-spacer"></span>
                <input
                  #dgFileInput
                  type="file"
                  accept=".docx"
                  class="dg-hidden-input"
                  (change)="onDiscussionGuideDocxChange($event.target)"
                />
                <button
                  type="button"
                  mat-stroked-button
                  color="primary"
                  [disabled]="processingDiscussionGuide"
                  (click)="dgFileInput.click()"
                >
                  <mat-icon>upload_file</mat-icon>
                  {{
                    processingDiscussionGuide
                      ? 'Processing...'
                      : ' Choose .docx'
                  }}
                </button>
                <button
                  type="button"
                  mat-flat-button
                  color="accent"
                  data-test-id="sheet-get-started-set-questions-manually-button"
                  [disabled]="processingDiscussionGuide"
                  (click)="startManual()"
                >
                  <mat-icon>edit</mat-icon>
                  Add manually
                </button>
              </div>
            } @else {
              <app-reorderable-expansion-panels
                [showErrors]="themesFormSubmitted"
              ></app-reorderable-expansion-panels>
              @if (themesFormSubmitted) {
                @if (themesControl.hasError('required')) {
                  <mat-error
                    data-test-id="sheet-get-started-error-theme-required"
                  >
                    At least one section and question must be added
                  </mat-error>
                }
                @if (themesControl.hasError('noThemeName')) {
                  <mat-error
                    data-test-id="sheet-get-started-error-no-theme-name"
                  >
                    Interview section name is required
                  </mat-error>
                }
                @if (themesControl.hasError('noQuestions')) {
                  <mat-error
                    data-test-id="sheet-get-started-error-no-questions"
                  >
                    At least one question is required for every interview
                    section
                  </mat-error>
                }
                @if (themesControl.hasError('noQuestionName')) {
                  <mat-error
                    data-test-id="sheet-get-started-error-no-question-name"
                  >
                    Question name is required for all questions
                  </mat-error>
                }
                @if (themesControl.hasError('duplicateThemeNames')) {
                  <mat-error
                    data-test-id="sheet-get-started-error-duplicate-theme-names"
                  >
                    Interview section names must be unique
                  </mat-error>
                }
                @if (themesControl.hasError('duplicateQuestionNames')) {
                  <mat-error
                    data-test-id="sheet-get-started-error-duplicate-question-names"
                  >
                    Question names must be unique within each interview section
                  </mat-error>
                }
              }
            }
          </div>
          <div
            class="preview flex flex-col gap-4 flexible-half-width-container"
            [class.collapsed]="collapsedPreview"
            [class.expanded]="!collapsedPreview"
          >
            <div class="flex flex-row gap-2 items-center">
              <i>{{ collapsedPreview ? 'Show preview' : 'Hide preview' }} </i>
              <mat-icon
                class="collapsable"
                data-test-id="sheet-get-started-toggle-preview"
                (click)="setCollapsedState()"
              >
                {{
                  collapsedPreview
                    ? 'keyboard_double_arrow_left'
                    : 'keyboard_double_arrow_right'
                }}
              </mat-icon>
            </div>
            <app-data-sheet
              data-test-id="sheet-get-started-discussion-guide-preview"
              [class.collapsed]="collapsedPreview"
              [sheet]="themes"
              [sheetHeaders]="sheetHeaders"
              [themeFormChanges$]="themeFormChange$"
            ></app-data-sheet>
          </div>
        </div>
        <div class="progress-container">
          <button
            mat-button
            matStepperPrevious
            data-test-id="sheet-get-started-move-to-previous-step"
          >
            Back
          </button>
          <ng-container *ngTemplateOutlet="helpLinkTemplate"></ng-container>
          <button
            mat-button
            matStepperNext
            data-test-id="sheet-get-started-move-to-next-step"
          >
            Next
          </button>
        </div>
      </form>
    </mat-step>
    <mat-step [stepControl]="answerConfigFormGroup">
      <form
        class="flex flex-col"
        [formGroup]="answerConfigFormGroup"
        (submit)="onThemesFormSubmit()"
      >
        <ng-template matStepLabel>Answer format</ng-template>
        <div class="getting-started-container flex flex-row gap-8">
          <div class="flex flex-col gap-4 basis-[50%] half-width-container">
            <div>
              <h3>
                Choose between summarized or verbatim answer formats for sheet
                <i>{{ sheetName.value }}</i>
              </h3>
              <p>
                You can change this setting after publishing - your sheet will
                then regenerate all expert answers.
              </p>
              <mat-radio-group formControlName="answerType">
                <div class="radio-container flex flex-col gap-4">
                  <mat-radio-button
                    value="summarised"
                    data-test-id="sheet-get-started-answer-format-summarized-option"
                  >
                    <div class="flex flex-col gap-1">
                      <b>Summarized (recommended)</b>
                      <span class="text-sm">
                        Focuses on the key points, keeping it concise and
                        readable. Original source material is available
                        alongside each summary for full context.
                      </span>
                    </div>
                  </mat-radio-button>
                  <mat-radio-button
                    value="verbatim"
                    data-test-id="sheet-get-started-answer-format-verbatim-option"
                  >
                    <div class="flex flex-col gap-1">
                      <b>Verbatim</b>
                      <span class="text-sm">
                        Extracts the response as close to the original wording
                        as possible.
                      </span>
                    </div>
                  </mat-radio-button>
                </div>
              </mat-radio-group>
            </div>
          </div>
          <div
            class="preview flex flex-col gap-4 basis-[50%] half-width-container"
          >
            <app-skeleton-verbatim-summarised></app-skeleton-verbatim-summarised>
          </div>
        </div>
        <div class="progress-container">
          <button
            mat-button
            matStepperPrevious
            data-test-id="sheet-get-started-move-to-previous-step"
          >
            Back
          </button>
          <ng-container *ngTemplateOutlet="helpLinkTemplate"></ng-container>
          <button
            mat-button
            matStepperNext
            data-test-id="sheet-get-started-move-to-next-step"
          >
            Next
          </button>
        </div>
      </form>
    </mat-step>
    <mat-step [stepControl]="additionalFieldsFormGroup">
      <form class="flex flex-col" [formGroup]="additionalFieldsFormGroup">
        <ng-template matStepLabel>Add additional fields</ng-template>
        <div class="additional-fields-container flex flex-row gap-8">
          <div class="flex flex-col gap-4 flexible-half-width-container">
            <div>
              <h3>
                Add additional fields for sheet <i>{{ sheetName.value }}</i>
              </h3>
              <p>
                Add fields to include in your data capture sheet. These fields
                will show under the expert's name in your sheet. When exported,
                they will appear as additional columns.
              </p>
            </div>
            <div class="additional-fields flex flex-col gap-8">
              <div class="flex flex-col gap-1">
                <b>Optional fields</b>
                <mat-checkbox
                  formControlName="geography"
                  data-test-id="sheet-get-started-additional-fields-geography"
                  >Geography</mat-checkbox
                >
                <mat-checkbox
                  formControlName="profileType"
                  data-test-id="sheet-get-started-additional-fields-profile-type"
                >
                  Profile type
                </mat-checkbox>
                <mat-checkbox
                  formControlName="dateAndTimeOfCall"
                  data-test-id="sheet-get-started-additional-fields-date-and-time-of-call"
                >
                  Date and time of call
                </mat-checkbox>
                <mat-checkbox
                  formControlName="recruitmentSource"
                  data-test-id="sheet-get-started-additional-fields-recruitment-source"
                >
                  <div class="flex flex-row items-center content-center gap-1">
                    <span>Recruitment source</span>
                    <mat-icon
                      class="info"
                      matTooltip="Displays the source for experts added through file upload"
                    >
                      info_outline
                    </mat-icon>
                  </div>
                </mat-checkbox>
              </div>
              <div class="flex flex-col gap-1">
                <b>Include in export</b>
                <mat-checkbox
                  formControlName="expertBio"
                  data-test-id="sheet-get-started-additional-fields-expert-biography"
                >
                  Expert biography
                </mat-checkbox>
              </div>
              <div class="flex flex-col gap-4">
                <b>Suggested question threshold</b>
                <mat-form-field>
                  <mat-label>Threshold</mat-label>
                  <input
                    matInput
                    type="number"
                    formControlName="suggestedQuestionThreshold"
                    min="2"
                    data-test-id="sheet-get-started-suggested-question-threshold"
                  />
                  <mat-hint
                    >Number of distinct interviews a question must appear in
                    before being surfaced as a suggestion</mat-hint
                  >
                  <mat-error>Minimum value is 2</mat-error>
                </mat-form-field>
              </div>
            </div>
          </div>
          <div
            class="preview flex flex-col basis-1 gap-4 flexible-half-width-container"
          >
            <i>Preview</i>
            <app-data-sheet
              [sheet]="themes"
              [sheetHeaders]="sheetHeaders"
              [themeFormChanges$]="themeFormChange$"
            ></app-data-sheet>
          </div>
        </div>
        <div class="progress-container">
          <button
            mat-button
            matStepperPrevious
            data-test-id="sheet-get-started-move-to-previous-step"
            [disabled]="creatingSheet"
          >
            Back
          </button>
          <ng-container *ngTemplateOutlet="helpLinkTemplate"></ng-container>
          <button
            mat-button
            matStepperNext
            data-test-id="sheet-get-started-publish-sheet"
            [disabled]="creatingSheet"
            (click)="complete()"
          >
            {{ creatingSheet ? 'Publishing sheet...' : 'Publish' }}
          </button>
        </div>
      </form>
    </mat-step>
  </mat-stepper>
}

<ng-template #helpLinkTemplate>
  <a
    class="help-link"
    href="https://techspert.com/hubfs/Techspert%20Data%20Capture%20-%20How-To%20Guide.pdf?utm_source=portal&utm_medium=referral&utm_campaign=data_capture_getting_started"
    target="_blank"
    rel="noopener noreferrer"
  >
    See the how-to guide for assistance setting up your Data Capture.
  </a>
</ng-template>
